import { useEffect, useRef } from "react";
import { instagramPosts } from "@/data/instagramPosts";

declare global {
  interface Window {
    instgrm?: { Embeds: { process: () => void } };
  }
}

const EMBED_SCRIPT = "https://www.instagram.com/embed.js";
// Largura em que o player é montado; ele é reduzido na tela para caber no cartão.
const EMBED_WIDTH = 330;

// O script oficial troca cada citação pelo player do Instagram, com o perfil de quem
// postou. O HTML fica fora do controle do React porque o script substitui esses elementos.
const embedHtml = (url: string) =>
  `<blockquote class="instagram-media" data-instgrm-permalink="${url}" data-instgrm-version="14"><a href="${url}" target="_blank" rel="noopener noreferrer">Ver esta postagem no Instagram</a></blockquote>`;

function loadEmbeds() {
  if (window.instgrm) {
    window.instgrm.Embeds.process();
    return;
  }
  if (document.querySelector(`script[src="${EMBED_SCRIPT}"]`)) return;
  const script = document.createElement("script");
  script.src = EMBED_SCRIPT;
  script.async = true;
  document.body.appendChild(script);
}

export default function InstagramShelf() {
  const section = useRef<HTMLElement>(null);
  const grid = useRef<HTMLDivElement>(null);

  // Só carrega o Instagram quando a seção está perto de aparecer na tela.
  useEffect(() => {
    const element = section.current;
    if (!element) return;
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      loadEmbeds();
    }, { rootMargin: "500px" });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Mantém o player proporcional à largura do cartão.
  useEffect(() => {
    const element = grid.current;
    const tile = element?.querySelector(".bz-insta-tile");
    if (!element || !tile) return;
    const resize = new ResizeObserver(() => element.style.setProperty("--insta-scale", String(tile.clientWidth / EMBED_WIDTH)));
    resize.observe(tile);
    return () => resize.disconnect();
  }, []);

  if (instagramPosts.length === 0) return null;

  return (
    <section ref={section} className="bz-shelf" aria-label="Postagens no Instagram">
      <header className="bz-shelf-head">
        <div>
          <p>No Instagram</p>
          <h2>Quem fala da Bozzetti</h2>
        </div>
      </header>
      <div className="bz-insta" ref={grid}>
        {instagramPosts.map(({ url, author }) => (
          <article key={url}>
            <div className="bz-insta-tile">
              <div className="bz-insta-frame" dangerouslySetInnerHTML={{ __html: embedHtml(url) }} />
            </div>
            <a href={`https://www.instagram.com/${author}/`} target="_blank" rel="noopener noreferrer">@{author}</a>
          </article>
        ))}
      </div>
    </section>
  );
}
